import { defaultAllSelection } from "services/constants/selections";
import { canUseCompanyContext } from "services/utils/companyAccess";
import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import {
  ArrowForwardRounded,
  CalendarMonthRounded,
  CelebrationRounded,
  FormatListBulletedRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { designColors } from "services/constants/designTokens";
import { ctaButtonSx, sectionCardSx, SectionHeader } from "./homeUi";
import { TeamAvailabilityCalendar } from "./TeamAvailabilityCalendar";
import sessionHandling from "services/utils/notificationUtils";
import { showToast } from "services/utils/Status";
import { EmptyState } from "components/CustomComponents/EmptyState";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";

// Only the top 4 upcoming leave entries preview inline in list view; "View
// all" (SectionHeader's actionLabel, wired further down) opens the full,
// unsliced list in a side panel — same pattern as Leave Balances' Upcoming
// Leave card (TeamZone/LeaveSummary/index.js).
const UPCOMING_LEAVE_PREVIEW_COUNT = 4;

// Distinct accent per leave TYPE (not per request), so "Casual Leave" and
// "Sick Leave" chips are always visually different from each other. Backend
// leave types do carry their own admin-configured leave_color (set in HR >
// Time Off Settings), but in practice that value has been coming back
// missing or identical across every type here, which collapsed every chip
// to the same flat teal fallback — so this hashes the leave type's own name
// into a fixed palette instead of trusting a per-request backend color.
const LEAVE_TYPE_COLOR_PALETTE = [
  "#2563EB", // blue
  "#16A34A", // green
  "#F97316", // orange
  "#8272C0", // purple
  "#EA6EE4", // pink
  "#0F766E", // teal
  "#F46070", // red
  "#4F46E5", // indigo
];

const hashString = (value) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = value.charCodeAt(index) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

const getLeaveTypeColor = (typeName) => {
  if (!typeName) return designColors.teal;
  return LEAVE_TYPE_COLOR_PALETTE[
    hashString(typeName) % LEAVE_TYPE_COLOR_PALETTE.length
  ];
};

const parseApiDate = (value) => {
  if (!value) return null;
  const [day, month, year] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDateRange = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return "-";
  if (start.isSame(end, "day")) {
    return start.format("DD MMM YYYY");
  }
  return `${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`;
};

// Shared between the inline 4-entry preview and the "View all" side panel's
// full list, so both stay visually identical instead of drifting apart.
const UpcomingLeaveRow = ({ row }) => (
  <Mui.Box
    sx={{
      border: `1px solid ${designColors.slate100}`,
      borderRadius: 2.5,
      p: 1.1,
      backgroundColor: designColors.white,
    }}
  >
    <Mui.Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1.25}
    >
      <Mui.Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
        <Mui.Avatar
          src={row.imageUrl}
          sx={{
            width: 38,
            height: 38,
            backgroundColor: designColors.tealTint,
            color: designColors.tealDark,
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {row.name
            .split(" ")
            .map((part) => part[0])
            .join("")
            .slice(0, 2)}
        </Mui.Avatar>
        <Mui.Box sx={{ minWidth: 0 }}>
          <Mui.Typography
            sx={{ fontSize: 14.5, fontWeight: 600, color: designColors.slate900 }}
          >
            {row.name}
          </Mui.Typography>
          <Mui.Typography sx={{ fontSize: 12.75, color: designColors.slate600 }}>
            {row.role} - {row.department}
          </Mui.Typography>
        </Mui.Box>
      </Mui.Stack>
      <Mui.Chip
        label={row.type}
        size="small"
        sx={{
          backgroundColor: `${row.color}20`,
          color: row.color,
          fontWeight: 700,
          flexShrink: 0,
        }}
      />
    </Mui.Stack>
    <Mui.Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      spacing={0.6}
      sx={{ pt: 0.9 }}
    >
      <Mui.Typography sx={{ fontSize: 13.25, color: designColors.slate800 }}>
        {formatDateRange(row.startDate, row.endDate)}
      </Mui.Typography>
      <Mui.Typography sx={{ fontSize: 12.75, color: designColors.slate600 }}>
        {row.halfDay
          ? `Half day (${row.whichHalf === "1st" ? "Morning" : "Afternoon"})`
          : `${row.businessDays || 1} day${row.businessDays > 1 ? "s" : ""}`}
      </Mui.Typography>
    </Mui.Stack>
  </Mui.Box>
);

const viewToggleSx = {
  height: 26,
  "& .MuiToggleButton-root": {
    px: 0.85,
    py: 0.25,
    color: designColors.slate500,
    border: `1px solid ${designColors.slate200}`,
    "&.Mui-selected": {
      backgroundColor: designColors.tealTint,
      color: designColors.tealDark,
    },
    "&.Mui-selected:hover": {
      backgroundColor: designColors.tealTint,
    },
  },
};

export const UpcomingLeaveSection = ({
  navigateToModule,
  loading: parentLoading = false,
  refreshKey = 0,
}) => {
  const { domain, token, userDetails, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );
  const storedUser = JSON.parse(localStorage.getItem("currentuser") || "{}");
  const userId = userDetails?.id || storedUser?.id;
  const companyId = topbarCompanyId;
  // Calendar is the default view on Home's initial render — the list view
  // (4-entry preview + "View all") is still reachable via the toggle above,
  // it's just no longer what greets someone landing on the page.
  const [view, setView] = useState("calendar");
  const [loading, setLoading] = useState(true);
  const [leaveEvents, setLeaveEvents] = useState([]);
  const [companyHolidayEvents, setCompanyHolidayEvents] = useState([]);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const {
    expanded: viewAllExpanded,
    toggleExpanded: toggleViewAllExpanded,
  } = useExpandablePanel(viewAllOpen);
  const isCalendarView = view === "calendar";
  const isLoading = loading || parentLoading;

  useEffect(() => {
    const fetchLeaveCalendar = async () => {
      if (!domain || !token || !userId || !canUseCompanyContext(companyId, userDetails?.type)) {
        // Keep the calendar in its loading state while refreshed auth/company
        // data is being restored, so it does not render an empty loaded view.
        setLoading(true);
        return;
      }

      setLoading(true);
      try {
        const leaveSummaryResponse = await fetch(
          `${domain}leavesummaryadmin/?department_id=${defaultAllSelection}&user_id=${userId}&company_id=${companyId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const companyLeaveResponse = await fetch(`${domain}companyleave/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });

        const leaveSummaryJson = await leaveSummaryResponse.json();
        const companyLeaveJson = await companyLeaveResponse.json();

        if (!leaveSummaryResponse.ok) {
          if (leaveSummaryResponse.status === 409) {
            sessionHandling();
            return;
          }
          throw new Error(leaveSummaryJson?.error || "LEAVE_SUMMARY_FAILED");
        }

        if (!companyLeaveResponse.ok) {
          if (companyLeaveResponse.status === 409) {
            sessionHandling();
            return;
          }
          throw new Error(companyLeaveJson?.error || "COMPANY_LEAVE_FAILED");
        }

        const mappedLeaveEvents = (leaveSummaryJson?.leave_count || [])
          .flatMap((user) =>
            (user.leave_requests || []).map((request) => {
              if (
                request.status !== "Approved" &&
                request.status !== "Completed"
              ) {
                return null;
              }

              const startDate = parseApiDate(request.from_date);
              const endDate = parseApiDate(request.to_date);
              if (!startDate || !endDate) {
                return null;
              }

              return {
                id: request.id,
                name: user.user_name,
                role:
                  user.role || user.type || user.designation || "Team member",
                department: user.department || "Team",
                type: request.leave_type,
                color: getLeaveTypeColor(request.leave_type),
                startDate,
                endDate,
                imageUrl: user.image_url || "",
                halfDay: request.half_day,
                whichHalf: request.which_half,
                businessDays: request.business_days || 0,
              };
            }),
          )
          .filter(Boolean)
          .sort((left, right) => left.startDate - right.startDate);

        const mappedCompanyHolidays = (companyLeaveJson || [])
          .map((holiday) => ({
            id: holiday.id,
            label: holiday.leave_type,
            date: parseApiDate(holiday.date),
          }))
          .filter((holiday) => holiday.date)
          .sort((left, right) => left.date - right.date);

        setLeaveEvents(mappedLeaveEvents);
        setCompanyHolidayEvents(mappedCompanyHolidays);
      } catch (error) {
        console.error("[UpcomingLeaveSection] operation failed:", error);
        showToast(
          "error",
          "Couldn't load the team leave calendar. Please refresh the page.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveCalendar();
    // refreshKey has no bearing on what to fetch — it's Home's Refresh
    // button giving this effect a reason to re-run, since this fetch is
    // otherwise independent of attendanceCounts (which the button already
    // reloads on its own).
  }, [domain, token, companyId, userId, refreshKey, userDetails?.type]);

  const today = dayjs().startOf("day");

  // leaveEvents is already sorted ascending by startDate (see the
  // mappedLeaveEvents sort above), so this filter alone keeps that order —
  // upcomingLeaveAll backs "View all"'s side panel, upcomingLeaveRows is
  // just its first 4 for the inline preview.
  const upcomingLeaveAll = useMemo(
    () =>
      leaveEvents.filter(
        (event) =>
          dayjs(event.endDate).startOf("day").valueOf() >= today.valueOf(),
      ),
    [leaveEvents, today],
  );

  const upcomingLeaveRows = useMemo(
    () => upcomingLeaveAll.slice(0, UPCOMING_LEAVE_PREVIEW_COUNT),
    [upcomingLeaveAll],
  );

  const upcomingHolidayRows = useMemo(
    () =>
      companyHolidayEvents
        .filter(
          (event) =>
            dayjs(event.date).startOf("day").valueOf() >= today.valueOf(),
        )
        .slice(0, 3),
    [companyHolidayEvents, today],
  );

  return (
    <>
    <Mui.Box
      sx={{
        ...sectionCardSx,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!isLoading && (
        <SectionHeader
          title={
            isCalendarView ? (
              <>
                Team Availability{" "}
                <Mui.Box
                  component="span"
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: designColors.slate500,
                  }}
                >
                  (This Month)
                </Mui.Box>
              </>
            ) : (
              "Upcoming Leave"
            )
          }
          tooltip={
            isCalendarView
              ? "A month view of your team's approved leave and company holidays, so you can spot overlaps at a glance."
              : "The soonest approved leave requests coming up for your team, with each request's dates and duration."
          }
          actionLabel={
            !isCalendarView &&
            upcomingLeaveAll.length > UPCOMING_LEAVE_PREVIEW_COUNT
              ? "View all"
              : undefined
          }
          onAction={() => setViewAllOpen(true)}
          endAdornment={
            <Mui.ToggleButtonGroup
              value={view}
              exclusive
              size="small"
              onChange={(event, nextView) => {
                if (nextView) setView(nextView);
              }}
              sx={viewToggleSx}
            >
              <Mui.ToggleButton value="calendar" aria-label="Calendar view">
                <CalendarMonthRounded sx={{ fontSize: 15 }} />
              </Mui.ToggleButton>
              <Mui.ToggleButton value="list" aria-label="List view">
                <FormatListBulletedRounded sx={{ fontSize: 15 }} />
              </Mui.ToggleButton>
            </Mui.ToggleButtonGroup>
          }
        />
      )}
      {isCalendarView ? (
        <TeamAvailabilityCalendar
          leaveEvents={leaveEvents}
          companyHolidayEvents={companyHolidayEvents}
          loading={isLoading}
        />
      ) : (
        <Mui.Stack spacing={1.1} sx={{ flex: 1, pt: 1.25 }}>
          {isLoading ? (
            <Mui.Stack spacing={1}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Mui.Skeleton
                  key={index}
                  variant="rounded"
                  height={64}
                  sx={{ borderRadius: 2.5 }}
                />
              ))}
            </Mui.Stack>
          ) : null}

          {!isLoading &&
            upcomingLeaveRows.map((row) => (
              <UpcomingLeaveRow key={`${row.name}-${row.id}`} row={row} />
            ))}

          {!isLoading && upcomingLeaveRows.length === 0 ? (
            <EmptyState
              title="No upcoming leave entries"
              subtitle="Approved team leave items will appear here automatically."
              size={96}
              sx={{ py: 2 }}
            />
          ) : null}

          {!isLoading && upcomingHolidayRows.length > 0 ? (
            <Mui.Box
              sx={{
                mt: 0.4,
                borderRadius: 2.5,
                backgroundColor: "#FFF9F0",
                border: "1px solid #FDE7C2",
                p: 1.15,
              }}
            >
              <Mui.Stack
                direction="row"
                alignItems="center"
                spacing={0.8}
                sx={{ mb: 0.8 }}
              >
                <CelebrationRounded sx={{ fontSize: 18, color: "#B45309" }} />
                <Mui.Typography
                  sx={{ fontSize: 13.5, fontWeight: 700, color: "#92400E" }}
                >
                  Company holidays
                </Mui.Typography>
              </Mui.Stack>
              <Mui.Stack spacing={0.65}>
                {upcomingHolidayRows.map((holiday) => (
                  <Mui.Stack
                    key={holiday.id}
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Mui.Typography
                      sx={{ fontSize: 13, color: designColors.slate800 }}
                    >
                      {holiday.label}
                    </Mui.Typography>
                    <Mui.Typography
                      sx={{ fontSize: 12.75, color: designColors.slate600 }}
                    >
                      {dayjs(holiday.date).format("DD MMM YYYY")}
                    </Mui.Typography>
                  </Mui.Stack>
                ))}
              </Mui.Stack>
            </Mui.Box>
          ) : null}
        </Mui.Stack>
      )}
      <Mui.Button
        fullWidth
        endIcon={<ArrowForwardRounded />}
        sx={{ ...ctaButtonSx, mt: 1.5 }}
        onClick={() => navigateToModule("Leave Balances")}
      >
        Go to Leave Balances
      </Mui.Button>
    </Mui.Box>

    <GlobalSidePanel
      open={viewAllOpen}
      onClose={() => setViewAllOpen(false)}
      expanded={viewAllExpanded}
      onToggleExpand={toggleViewAllExpanded}
      title="Upcoming Leave"
      subtitle={`${upcomingLeaveAll.length} upcoming leave${
        upcomingLeaveAll.length === 1 ? "" : "s"
      }`}
    >
      <Mui.Stack spacing={1.1} sx={{ p: 2.5 }}>
        {upcomingLeaveAll.length ? (
          upcomingLeaveAll.map((row) => (
            <UpcomingLeaveRow key={`${row.name}-${row.id}`} row={row} />
          ))
        ) : (
          <EmptyState
            title="No upcoming leave entries"
            subtitle="Approved team leave items will appear here automatically."
            size={96}
          />
        )}
      </Mui.Stack>
    </GlobalSidePanel>
    </>
  );
};
